import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { Button, Dropdown, Icon, Input, Popup } from 'semantic-ui-react';
import { UserContext } from '../context/User';
import {
  API,
  getLogo,
  getSystemName,
  isAdmin,
  showSuccess,
} from '../helpers';
import SinoTokenBackground from './SinoTokenBackground';
import './UserConsoleLayout.css';

const baseNavItems = [
  { to: '/dashboard', icon: 'chart bar', label: '数据看板', keywords: ['总览', '看板', 'dashboard'] },
  { to: '/dashboard/playground', icon: 'gamepad', label: '体验中心', keywords: ['体验', 'playground', '对话', '视频'] },
  { to: '/access', icon: 'code', label: '接入中心', keywords: ['接入', '文档', 'api', 'sdk', 'curl'] },
  { to: '/token', icon: 'key', label: 'API 令牌', keywords: ['令牌', 'token', 'key'] },
  { to: '/subscriptions', icon: 'archive', label: '已订购', keywords: ['订购', '已订购', '资源包', '套餐', 'subscription'] },
  { to: '/topup', icon: 'credit card', label: '充值', keywords: ['充值', '余额', 'topup'] },
  { to: '/log', icon: 'book', label: '调用日志', keywords: ['日志', '调用', 'log'] },
  { to: '/setting', icon: 'setting', label: '设置', keywords: ['设置', '账号', 'setting'] },
];

const adminNavItems = [
  { to: '/model', icon: 'cubes', label: '模型管理', keywords: ['模型', 'model'] },
  { to: '/channel', icon: 'sitemap', label: '渠道管理', keywords: ['渠道', 'channel'] },
  { to: '/operator', icon: 'server', label: '运营管理', keywords: ['运营', 'operator'] },
  { to: '/user', icon: 'users', label: '用户管理', keywords: ['用户', 'user'] },
  { to: '/redemption', icon: 'gift', label: '兑换管理', keywords: ['兑换', 'redemption'] },
];

function safeUserFromStorage() {
  try {
    return JSON.parse(localStorage.getItem('user') || '{}');
  } catch (error) {
    return {};
  }
}

function getPageTitle(pathname, navItems) {
  const matched = navItems
    .filter((item) => pathname === item.to || pathname.startsWith(`${item.to}/`))
    .sort((a, b) => b.to.length - a.to.length)[0];

  if (matched) {
    if (pathname.includes('/add')) return `新增${matched.label.replace('管理', '')}`;
    if (pathname.includes('/edit')) return `编辑${matched.label.replace('管理', '')}`;
    return matched.label;
  }

  return '工作台';
}

const UserConsoleLayout = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [userState, userDispatch] = useContext(UserContext);
  const [searchText, setSearchText] = useState('');
  const systemName = getSystemName();
  const logo = getLogo();
  const user = userState.user || safeUserFromStorage();

  useEffect(() => {
    document.body.classList.add('sino-console-shell');
    return () => {
      document.body.classList.remove('sino-console-shell');
    };
  }, []);

  const navItems = useMemo(() => {
    if (isAdmin()) {
      return [...baseNavItems.slice(0, 1), ...adminNavItems, ...baseNavItems.slice(1)];
    }
    return baseNavItems;
  }, []);

  const pageTitle = getPageTitle(location.pathname, navItems);

  const logout = async () => {
    await API.get('/api/user/logout');
    userDispatch({ type: 'logout' });
    localStorage.removeItem('user');
    showSuccess('注销成功!');
    navigate('/login');
  };

  const handleSearch = () => {
    const keyword = searchText.trim().toLowerCase();
    if (!keyword) return;

    const matched = navItems.find((item) =>
      item.keywords.some((candidate) => keyword.includes(candidate.toLowerCase()))
    );

    navigate(matched ? matched.to : '/log');
    setSearchText('');
  };

  return (
    <div className='sino-console'>
      <SinoTokenBackground />
      <aside className='sino-console-rail'>
        <Link to='/dashboard' className='sino-rail-logo' aria-label={systemName}>
          <img src={logo} alt={systemName} />
        </Link>
        <nav className='sino-rail-nav'>
          {navItems.map((item) => {
            const active =
              location.pathname === item.to || location.pathname.startsWith(`${item.to}/`);
            return (
              <Popup
                key={item.to}
                content={item.label}
                position='right center'
                trigger={
                  <Button
                    as={Link}
                    to={item.to}
                    icon
                    className={active ? 'active' : ''}
                    aria-label={item.label}
                  >
                    <Icon name={item.icon} />
                  </Button>
                }
              />
            );
          })}
        </nav>
      </aside>

      <div className='sino-console-main'>
        <header className='sino-console-topbar'>
          <h1>{pageTitle}</h1>
          <div className='sino-topbar-actions'>
            <Input
              icon='search'
              iconPosition='left'
              placeholder='搜索'
              value={searchText}
              onChange={(_, { value }) => setSearchText(value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  handleSearch();
                }
              }}
            />
            <Dropdown
              direction='left'
              trigger={
                <Button icon className='sino-topbar-button is-account' aria-label='账户菜单'>
                  <Icon name='user outline' />
                </Button>
              }
            >
              <Dropdown.Menu>
                <Dropdown.Header content={user.display_name || user.username || '用户'} />
                <Dropdown.Item icon='setting' text='账户设置' onClick={() => navigate('/setting')} />
                <Dropdown.Item icon='sign out' text='退出登录' onClick={logout} />
              </Dropdown.Menu>
            </Dropdown>
          </div>
        </header>

        <main className='sino-console-content'>
          <div className='sino-console-tab'>{pageTitle}</div>
          <div className='sino-console-page'>{children || <Outlet />}</div>
        </main>
      </div>

      <Link to='/about' className='sino-contact-floating'>
        <span>联系我们</span>
        <Icon name='comment dots' />
      </Link>
    </div>
  );
};

export default UserConsoleLayout;
